/**
 * The file spaces as the user knows them (`docs/adr-two-file-spaces.md`, decision 10): the code
 * keeps `workspace:` and `drive:`.
 */
const SPACE_NAMES: Record<string, string> = {
  workspace: 'Files in the Dossier',
  drive: 'Files in the Drive',
  plugin: 'Its own files',
};

/** A `files:<space>:<glob>` permission's readable form (`Files in the Dossier: notes/*.md`). */
function filesLabel(scope: string): string {
  const separator = scope.indexOf(':');
  const space = separator === -1 ? undefined : SPACE_NAMES[scope.slice(0, separator)];
  if (space === undefined) return `Files ${scope}`;
  const glob = scope.slice(separator + 1);
  return glob === '**' ? space : `${space}: ${glob}`;
}

/** Readable names for what a plugin may do, as its page and the install prompt show them. */
export function permissionLabel(permission: string): string {
  if (permission.startsWith('network:')) return `Reach ${permission.slice('network:'.length)}`;
  if (permission.startsWith('files:')) return filesLabel(permission.slice('files:'.length));
  const labels: Record<string, string> = {
    files: 'Files',
    storage: 'Storage',
    triggers: 'Timers and webhooks',
    connections: 'Your connections',
    'connections.actions': 'Run service actions with your connections',
    agents: 'Run agents',
    'agents.unattended': 'Run agents without asking',
    tools: 'Other plugins’ tools',
    events: 'Events and notifications',
    voice: 'Voice calls',
    inference: 'Your model provider',
    browser: 'The browser',
    commands: 'Commands on this machine',
    widgets: 'Widgets and your Crowdex activity',
    'media.camera': 'Camera',
    'media.microphone': 'Microphone',
  };
  return labels[permission] ?? permission;
}

/** One line of the install prompt for a grant (`manifestGrants`). */
export function grantLabel(grant: string): string {
  const [kind, ...rest] = grant.split(':');
  const value = rest.join(':');
  switch (kind) {
    case 'permission':
      return permissionLabel(value);
    case 'connection':
      return `Your ${value} connection`;
    case 'type':
      return `Owns ${value.split(':')[0]} files`;
    case 'agent':
      return 'Adds an agent';
    case 'command': {
      try {
        const [program, ...args] = JSON.parse(value) as [
          { npm?: string; binary?: string },
          ...string[],
        ];
        return `Runs ${[program.npm ?? program.binary ?? '?', ...args].join(' ')}`;
      } catch {
        return 'Runs a command';
      }
    }
    case 'mcp':
      return `Tools from ${value}`;
    default:
      return grant;
  }
}

export function GrantList({ grants, label }: { grants: readonly string[]; label: string }) {
  if (grants.length === 0) return <p className="text-muted-foreground text-xs">Nothing.</p>;
  return (
    <ul aria-label={label} className="flex flex-wrap gap-1">
      {grants.map((grant) => (
        <li className="rounded-md bg-muted px-1.5 py-0.5 text-xs" key={grant}>
          {grantLabel(grant)}
        </li>
      ))}
    </ul>
  );
}
